/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import {Group, StorageUtils, EEmbededElementTypes, RenderDocument} from "js_renderer";
import {vec2, vec3} from "gl-matrix";
import PointEntity from "./point_entity";
import Factory from "../factory";

export default
class MeasurePointEntity extends PointEntity {

    public static TypeName: string = EEmbededElementTypes.MEASUREPOINTENTITY;
    public static texture_size: number[] = [256, 256];

    constructor(aRenderDocument: RenderDocument) {
        super(aRenderDocument);

        let url = '@assets/images/points_texture.png';

        // glyph 16 x 16 px in a texture [256 x 256] px
        this.extendProperties({
            textureUrl: url,
            textureSize: vec2.clone(MeasurePointEntity.texture_size),
            textureHasAlpha: true,
            minPointSize: 16.0,
            maxPointSize: 16.0,
            glyphPosition: [128 / MeasurePointEntity.texture_size[0], 0 / MeasurePointEntity.texture_size[1]],
            glyphSize: [16.0 / MeasurePointEntity.texture_size[0], 16.0 / MeasurePointEntity.texture_size[1]],
            alwaysOnTop: false,
            pickable: true,
            pinPoint: [0.0, 0.0]
        });
    }

    public static create(aRenderDocument: RenderDocument): MeasurePointEntity {
        let element = new MeasurePointEntity(aRenderDocument);
        element._typeName = MeasurePointEntity.TypeName;
        return element;
    }

    public extendProperties(aProperties: any) {
        // extend the properties
        super.extendProperties(aProperties);

        if (this._properties.glyphIndex === undefined) {
            return;
        }

        if (this._properties.glyphSize === undefined) {
            return;
        }

        let gi = this._properties.glyphIndex;

        // compute glyph position in pixels
        let row = Math.floor(gi / this._properties.glyphsInRow);
        let col = gi - (row * this._properties.glyphsInRow);
        this._properties.glyphPosition = [col * this._properties.glyphSize[0], row * this._properties.glyphSize[1]];
    }

    /***
     * Set size of the point
     *
     * @param aSize
     * @param aMinSize
     * @param aMaxSize
     */
    public setSize(aMinSize: number, aMaxSize: number) {
        this._properties.minPointSize = aMinSize;
        this._properties.maxPointSize = aMaxSize;
    }

    /***
     * Set an offset by which the rendered point will be moved within the rendered point size. The range of the input value is [0..1, 0..1].
     * @param {vec2} aValue For example if we set the offset to [0.0, 0.5] then we move the center pixel up by half of the point size
     *                      resulting to the state that the bottom pixel of the point will be rendered at the position of the point.
     */
    public setPinPoint(aValue: vec2) {
        this._properties.pinPoint = vec2.clone(aValue);
    }

    public setBlendColorFromGroup(aGroup: Group) {
        return; // TB: do not use this for measure points

        if (!aGroup) {
            return;
        }

        // if group color is defined, use that color as blend color of the poi, otherwise use the base color of the poi to do the blending
        let groupColor = aGroup.getColor();
        if (groupColor !== '' && groupColor !== "#000000") {
            this._properties.baseColor = aGroup.getColorRGBf();
        }
    }

// TODO: @ Jarek: the model element can contain more than one poi part, so we should generate more poi geometry for such model element
    protected _generateElementDescriptionJSON(): any {
        // these are model element properties taken from the scene api element properties
        return {
            type: this.getTypeName(),
            id: this.getTypeName() + "_" + this._UID,   // this is is not important in the engine, it can identify e.g. id of an element in Lexocad
            UID: this._UID
        };
    }

// TODO: @ Jarek: the model element can contain more than one poi part, so we should generate more poi geometry for such model element
    protected _generateGeometryObjectDescriptionJSON(): any {
        return [{
            description: {
                type: "POINT",
                bboxMin: [this._properties.position[0], this._properties.position[1], this._properties.position[2]],
                bboxMax: [this._properties.position[0], this._properties.position[1], this._properties.position[2]]
            },
            appearance: {
                material: {
                    diffuseColor: this._properties.baseColor
                },
                pointProperties: {
                    minPointSize: this._properties.minPointSize,
                    maxPointSize: this._properties.maxPointSize,
                    glyphPos: this._properties.glyphPosition,
                    glyphSize: this._properties.glyphSize,
                    pinPoint: this._properties.pinPoint
                },
                renderOption: {
                    alwaysOnTop: this._properties.alwaysOnTop,
                    pickable: this._properties.pickable
                },
                imageTexture: {
                    url: [this._properties.textureUrl],
                    width: [this._properties.textureSize[0]],
                    height: [this._properties.textureSize[1]],
                    offsets: [0],
                    sizes: [0],
                    tiled: [false],
                    alpha: this._properties.textureHasAlpha,
                    model: "MODULATE",                          // TB: modulate with baseColor (original point in texture is white
                    blendColor: this._properties.baseColor,     //     so this sets the point color to one loaded from ModelDescription
                    repeatS: false,
                    repeatT: false
                }
            },
            pointSet: {
                src: this._renderDocument.getRenderer()._dataStorage.createLocalResourceUri(),
                vertexCount: 1,
                binSize: 12,
                points: this._properties.position
            }
        }];

    }
}

Factory.register(MeasurePointEntity);
